class InfiniteScroll {
  constructor(scopeElement = null) {
    // Store the scope element (tab content) for this instance
    // This ensures this instance always operates on the same tab/collection
    this.scopeElement = scopeElement || window.activeCollectionTab || document;
    
    // Helper method to get the correct scope (active tab or document)
    this.getScope = function() {
      // Always use the scope that was set during construction
      return this.scopeElement;
    };
    
    const scope = this.getScope();
    this.loadMoreBtn = scope.querySelector('[data-load-more]');
    this.productGrid = scope.querySelector('#product-grid');
    this.currentCountElement = scope.querySelector('[data-current-count]');
    this.totalCountElement = scope.querySelector('[data-total-count]');
    this.progressFill = scope.querySelector('[data-progress-fill]');
    
    // Select loading text elements relative to the button
    this.loadingText = this.loadMoreBtn ? this.loadMoreBtn.querySelector('[data-loading-text]') : null;
    this.loadText = this.loadMoreBtn ? this.loadMoreBtn.querySelector('[data-load-text]') : null;
    this.isLoading = false;
    this.currentPage = 1;
    this.loadedProductCount = 0;
    this.initialized = false;
    
    this.init();
  }

  init() {
    if (!this.loadMoreBtn) {
      return;
    }
    
    if (this.initialized) {
      return;
    }
    
    // Mark button as initialized to prevent double initialization
    if (this.loadMoreBtn.dataset.infiniteScrollInit === 'true') {
      return;
    }
    this.loadMoreBtn.dataset.infiniteScrollInit = 'true';
    
    // Use event delegation on document to catch clicks - this bypasses any CSS blocking issues
    this.boundHandleLoadMore = this.handleLoadMore.bind(this);
    this.documentClickHandler = (e) => {
      // Check if click is on or inside the load more button
      const loadMoreBtn = e.target.closest('[data-load-more]');
      if (loadMoreBtn) {
        this.boundHandleLoadMore(e);
      }
    };
    
    // Attach to document with capture to catch it early
    document.addEventListener('click', this.documentClickHandler, true);
    
    this.initialized = true;
    
    // Initialize current counts from DOM
    if (this.currentCountElement) {
      this.loadedProductCount = parseInt(this.currentCountElement.textContent) || 0;
    }
    
    // Update current page from URL
    const urlParams = new URLSearchParams(window.location.search);
    const pageParam = urlParams.get('page');
    if (pageParam) {
      this.currentPage = parseInt(pageParam);
    }
    
  }

  async handleLoadMore(event) {
    // Prevent any default behavior
    event.preventDefault();
    event.stopPropagation();
    
    if (this.isLoading) {
      return;
    }
    
    // Set a global flag to prevent facets.js from interfering
    window.infiniteScrollLoading = true;
    
    this.isLoading = true;
    this.showLoading();
    
    try {
      // Get the next page URL from the data attribute
      const nextPageUrl = this.loadMoreBtn.dataset.nextPageUrl;
      
      if (!nextPageUrl) {
        console.error('No next page URL found');
        this.hideLoadMoreButton();
        return;
      }
      
      // Parse the URL to ensure we have the full URL
      const url = new URL(nextPageUrl, window.location.origin);
      
      // Fetch the next page via AJAX
      const response = await fetch(url.href, {
        method: 'GET',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const html = await response.text();
      
      // Parse the response to extract products
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      console.log(doc)
      const newProductGrid = doc.querySelector('#product-grid');
      
      if (!newProductGrid) {
        console.error('No product grid found in response');
        return;
      }
      
      const newProducts = newProductGrid.querySelectorAll('.grid__item');
      const nextLoadMoreBtn = doc.querySelector('[data-load-more]');
      
      if (newProducts.length > 0) {
        // Add new products to the grid
        this.appendNewProducts(newProducts);
        
        // Update counters and progress
        this.updateCounters(newProducts.length);
        this.updateProgress();
        
        // Update or hide load more button
        if (nextLoadMoreBtn) {
          this.updateLoadMoreButton(nextLoadMoreBtn);
        } else {
          this.hideLoadMoreButton();
        }
        
        // Apply rendering logic from facets.js
        this.renderNewProducts(newProducts);
        
        this.currentPage++;
      } else {
        // No more products to load
        this.hideLoadMoreButton();
      }
      
    } catch (error) {
      console.error('Error loading more products:', error);
      this.showError();
    } finally {
      this.isLoading = false;
      this.hideLoading();
      // Clear the flag
      window.infiniteScrollLoading = false;
    }
  }

  appendNewProducts(newProducts) {
    // ALWAYS get a fresh reference to the product grid since facets.js might replace it
    const scope = this.getScope();
    const currentGrid = scope.querySelector('#product-grid');
    
    if (!currentGrid) {
      console.error('Product grid not found in DOM! Cannot append products.');
      return;
    }
    
    // Update our cached reference
    this.productGrid = currentGrid;
    
    // Ensure loading overlay is hidden before appending
    const loadingOverlay = scope.querySelector('.loading-overlay');
    if (loadingOverlay) {
      loadingOverlay.classList.add('hidden');
    }
    
    // Clone and append new products to the grid
    newProducts.forEach((product) => {
      const clonedProduct = product.cloneNode(true);
      this.productGrid.appendChild(clonedProduct);
    });
  }

  renderNewProducts(newProducts) {
    // Get the newly added products from the actual DOM
    const allProducts = this.productGrid.querySelectorAll('.grid__item');
    const newlyAddedProducts = Array.from(allProducts).slice(-newProducts.length);
    
    // Apply the same rendering logic used in facets.js
    newlyAddedProducts.forEach(product => {
      // Handle scroll trigger animations (similar to facets.js)
      const scrollTriggers = product.querySelectorAll('.scroll-trigger');
      scrollTriggers.forEach(element => {
        element.classList.add('scroll-trigger--cancel');
      });
      
      // Handle lazy loading for images
      this.handleLazyLoading(product);
      
      // Trigger currency conversion if available
      if (window.convertCurrency) {
        window.convertCurrency(product);
      }
    });

    // Initialize scroll animations if enabled - pass the actual product grid container
    if (typeof initializeScrollAnimationTrigger === 'function') {
      initializeScrollAnimationTrigger(this.productGrid);
    }

    // Reinitialize any product-specific JavaScript
    this.reinitializeProductScripts(newlyAddedProducts);
  }

  handleLazyLoading(container) {
    // Trigger lazy loading for dynamically loaded product images (from facets.js)
    if (window.lazyloadPolyfill) {
      window.lazyloadPolyfill(container);
    }
    
    // Also trigger lazysizes if available for lazyload class images (from facets.js)
    if (window.lazySizes) {
      // Trigger lazysizes to check for new lazyload elements
      container.querySelectorAll('img.lazyload').forEach((img) => {
        window.lazySizes.loader.unveil(img);
      });
      // Also trigger a general recheck for any missed elements
      window.lazySizes.autoSizer.checkElems();
    } else {
      // Fallback if lazySizes isn't loaded yet - manually process lazyload images
      container.querySelectorAll('img.lazyload').forEach((img) => {
        if (img.dataset.src && !img.src) {
          img.src = img.dataset.src;
        }
        if (img.dataset.srcset && !img.srcset) {
          img.srcset = img.dataset.srcset;
        }
        img.classList.remove('lazyload');
        img.classList.add('lazyloaded');
      });
      
      // Also handle images that might not have lazyload class but need src from data-src
      container.querySelectorAll('img[data-src]:not([src]), img[src=""]:not(.lazyloaded)').forEach((img) => {
        if (img.dataset.src) {
          img.src = img.dataset.src;
        }
        if (img.dataset.srcset) {
          img.srcset = img.dataset.srcset;
        }
      });
    }
  }

  showLoading() {
    // Get fresh reference to button
    const scope = this.getScope();
    this.loadMoreBtn = scope.querySelector('[data-load-more]');
    
    if (!this.loadMoreBtn) {
      console.warn('Load more button not found in showLoading');
      return;
    }
    
    this.loadMoreBtn.disabled = true;
    
    // Get fresh references to text elements
    this.loadText = this.loadMoreBtn.querySelector('[data-load-text]');
    this.loadingText = this.loadMoreBtn.querySelector('[data-loading-text]');
    
    // Hide normal text
    if (this.loadText) {
      this.loadText.style.display = 'none';
    }
    
    // Show loading text
    if (this.loadingText) {
      this.loadingText.style.display = 'inline-block';
    } else {
      // Fallback: if loading text element not found, update button text directly
      this.loadMoreBtn.innerHTML = '<span class="loading-spinner"></span>LOADING...';
    }
    
    // Make sure loading overlay is hidden so products can be seen
    const loadingOverlay = scope.querySelector('.loading-overlay');
    if (loadingOverlay) {
      loadingOverlay.classList.add('hidden');
    }
  }

  hideLoading() {
    // Get fresh reference to button
    const scope = this.getScope();
    this.loadMoreBtn = scope.querySelector('[data-load-more]');
    
    if (!this.loadMoreBtn) {
      console.warn('Load more button not found in hideLoading');
      return;
    }
    
    this.loadMoreBtn.disabled = false;
    
    // Get fresh references to text elements
    this.loadText = this.loadMoreBtn.querySelector('[data-load-text]');
    this.loadingText = this.loadMoreBtn.querySelector('[data-loading-text]');
    
    // Show normal text
    if (this.loadText) {
      this.loadText.style.display = 'inline-block';
    }
    
    // Hide loading text
    if (this.loadingText) {
      this.loadingText.style.display = 'none';
    } else {
      // Fallback: restore original button content
      const originalContent = `
        <span data-load-text>LOAD MORE</span>
        <span data-loading-text style="display: none;">
          <span class="loading-spinner"></span>LOADING...
        </span>
      `;
      this.loadMoreBtn.innerHTML = originalContent;
      
      // Re-select text elements after restoration
      this.loadingText = this.loadMoreBtn.querySelector('[data-loading-text]');
      this.loadText = this.loadMoreBtn.querySelector('[data-load-text]');
    }
  }

  updateCounters(newProductCount) {
    this.loadedProductCount += newProductCount;
    
    // Get fresh reference to counter in case it was replaced
    const scope = this.getScope();
    this.currentCountElement = scope.querySelector('[data-current-count]');
    
    if (this.currentCountElement) {
      this.currentCountElement.textContent = this.loadedProductCount;
    }
  }

  updateProgress() {
    // Get fresh references
    const scope = this.getScope();
    this.progressFill = scope.querySelector('[data-progress-fill]');
    this.totalCountElement = scope.querySelector('[data-total-count]');
    
    if (!this.progressFill || !this.totalCountElement) {
      return;
    }
    
    const totalCount = parseInt(this.totalCountElement.textContent) || 0;
    if (totalCount > 0) {
      const percentage = (this.loadedProductCount / totalCount) * 100;
      this.progressFill.style.width = `${Math.min(percentage, 100)}%`;
    }
  }

  updateLoadMoreButton(nextLoadMoreBtn) {
    // Update the button URL to point to the next page
    if (nextLoadMoreBtn.dataset.nextPageUrl) {
      let nextPageUrl = nextLoadMoreBtn.dataset.nextPageUrl;
      
      // Preserve wishlist parameter if it exists in the current URL
      const currentUrl = new URL(this.loadMoreBtn.dataset.nextPageUrl || window.location.href, window.location.origin);
      const hasWishlist = currentUrl.searchParams.has('wishlist');
      
      if (hasWishlist) {
        // Ensure the next URL also has the wishlist parameter
        const nextUrl = new URL(nextPageUrl, window.location.origin);
        if (!nextUrl.searchParams.has('wishlist')) {
          nextUrl.searchParams.set('wishlist', 'true');
          nextPageUrl = nextUrl.pathname + nextUrl.search;
        }
      }
      
      this.loadMoreBtn.dataset.nextPageUrl = nextPageUrl;
    }
    
    // Update data attributes if they exist
    if (nextLoadMoreBtn.dataset.nextPage) {
      this.loadMoreBtn.dataset.nextPage = nextLoadMoreBtn.dataset.nextPage;
    }
    if (nextLoadMoreBtn.dataset.loadedCount) {
      this.loadMoreBtn.dataset.loadedCount = nextLoadMoreBtn.dataset.loadedCount;
    }
    
    // Re-select text elements in case the button content changed
    this.loadingText = this.loadMoreBtn.querySelector('[data-loading-text]');
    this.loadText = this.loadMoreBtn.querySelector('[data-load-text]');
  }

  hideLoadMoreButton() {
    if (this.loadMoreBtn) {
      this.loadMoreBtn.style.display = 'none';
    }
  }

  showError() {
    const errorMessage = document.createElement('div');
    errorMessage.className = 'infinite-scroll-error';
    errorMessage.innerHTML = 'Failed to load more products. Please try again.';
    errorMessage.style.cssText = 'color: red; text-align: center; margin: 1rem 0;';
    
    this.loadMoreBtn.parentNode.insertBefore(errorMessage, this.loadMoreBtn);
    
    // Remove error message after 3 seconds
    setTimeout(() => {
      errorMessage.remove();
    }, 3000);
  }

  reinitializeProductScripts(newProducts) {
    // Reinitialize quick add functionality for new products
    if (window.theme && window.theme.quickAdd) {
      window.theme.quickAdd.init();
    }
    
    // Reinitialize product forms for new products
    newProducts.forEach(product => {
      // Skip products that have already been initialized
      if (product.dataset.initialized) return;
      
      const newProductForms = product.querySelectorAll('.product-form:not([data-initialized])');
      newProductForms.forEach(form => {
        if (window.theme && window.theme.ProductForm) {
          new window.theme.ProductForm(form);
          form.dataset.initialized = 'true';
        }
      });
      
      // Reinitialize any other product-specific functionality
      if (window.theme && window.theme.ProductCard) {
        const newProductCards = product.querySelectorAll('.card-product:not([data-initialized])');
        newProductCards.forEach(card => {
          window.theme.ProductCard.init(card);
          card.dataset.initialized = 'true';
        });
      }
      
      // Reinitialize swatch functionality
      const newSwatches = product.querySelectorAll('.product-option-swatch:not([data-initialized])');
      newSwatches.forEach(swatch => {
        if (window.theme && window.theme.ProductSwatch) {
          window.theme.ProductSwatch.init(swatch);
          swatch.dataset.initialized = 'true';
        }
      });

      // Reinitialize variant pickers
      const variantPickers = product.querySelectorAll('variant-selects:not([data-initialized])');
      variantPickers.forEach(picker => {
        if (window.customElements && window.customElements.get('variant-selects')) {
          picker.dataset.initialized = 'true';
        }
      });

      // Reinitialize quantity inputs
      const quantityInputs = product.querySelectorAll('quantity-input:not([data-initialized])');
      quantityInputs.forEach(input => {
        if (window.customElements && window.customElements.get('quantity-input')) {
          input.dataset.initialized = 'true';
        }
      });

      // Reinitialize modal triggers
      const modalTriggers = product.querySelectorAll('[data-modal-trigger]:not([data-initialized])');
      modalTriggers.forEach(trigger => {
        if (window.theme && window.theme.modals) {
          window.theme.modals.init(trigger);
          trigger.dataset.initialized = 'true';
        }
      });
      
      // Reinitialize details accordion (to fix the error)
      const detailsElements = product.querySelectorAll('details[id]:not([data-initialized])');
      detailsElements.forEach(details => {
        if (window.customElements && window.customElements.get('details-accordion')) {
          details.dataset.initialized = 'true';
        }
      });
      
      // Mark this product as initialized
      product.dataset.initialized = 'true';
    });
  }
}

// Singleton pattern to prevent multiple instances
// Expose globally so collection tabs can reset it
window.infiniteScrollInstance = null;

function initInfiniteScroll() {
  // Only initialize if we have a load more button and haven't already initialized
  const loadMoreBtn = document.querySelector('[data-load-more]');
  
  if (loadMoreBtn && !window.infiniteScrollInstance) {
    window.infiniteScrollInstance = new InfiniteScroll();
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initInfiniteScroll);
} else {
  // DOM is already loaded
  initInfiniteScroll();
}

// Also initialize for theme editor (but reset instance first)
document.addEventListener('shopify:section:load', () => {
  window.infiniteScrollInstance = null;
  initInfiniteScroll();
}); 